import { Calendar, Clock, Timer, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

const SCHEDULE_PREFS = [
  ['today', 'Today'],
  ['today-time', 'Today at time'],
  ['tomorrow', 'Tomorrow'],
  ['pick-datetime', 'Pick date & time'],
];
const ESTIMATE_OPTIONS = [2, 15, 30, 45, 60, 90];

/**
 * Scheduling block of the task form: when it appears, estimate (with the
 * GTD 2-minute-rule nudge + live countdown timer), and due date.
 */
export function TaskScheduleSection({
  form, setForm, todayDate, tomorrowDate,
  twoMinTimer, setTwoMinTimer, twoMinDismissed, setTwoMinDismissed, onSave,
}) {
  return (
    <>
      <div>
        <label className="text-[10px] uppercase tracking-[0.08em] font-semibold block mb-1.5" style={{ color: 'var(--pd-subtle)' }}>When should this appear?</label>
        <div className="flex flex-wrap gap-1.5">
          {SCHEDULE_PREFS.map(([key, label]) => (
            <button key={key} type="button" data-testid={`task-schedule-pref-${key}`} onClick={() => {
              const scheduledDateTime = key === 'tomorrow' ? `${tomorrowDate}T09:00` : key === 'today-time' ? `${todayDate}T09:00` : form.scheduledDateTime;
              setForm((prev) => ({ ...prev, schedulePreference: key, scheduledDateTime }));
            }} className="px-3 py-2 rounded-xl text-[11px] font-medium" style={{ background: form.schedulePreference === key ? 'var(--pd-focus)' : 'var(--pd-panel-2)', color: form.schedulePreference === key ? 'white' : 'var(--pd-muted)' }}>{label}</button>
          ))}
        </div>
        {(form.schedulePreference === 'today-time' || form.schedulePreference === 'pick-datetime') && (
          <input data-testid="task-scheduled-datetime-input" type="datetime-local" value={form.scheduledDateTime} onChange={(e) => setForm((prev) => ({ ...prev, scheduledDateTime: e.target.value }))} className="w-full mt-3 px-3 py-3 rounded-[16px] text-sm" style={{ border: '1px solid var(--pd-border)', background: 'var(--pd-panel-solid)' }} />
        )}
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <div>
          <label className="text-[10px] uppercase tracking-[0.08em] font-semibold block mb-1.5" style={{ color: 'var(--pd-subtle)' }}><Clock className="w-3 h-3 inline mr-1" />Estimate</label>
          <div className="flex gap-1.5">
            {ESTIMATE_OPTIONS.map((minutes) => (
              <button key={minutes} type="button" onClick={() => { setForm((prev) => ({ ...prev, estimatedMinutes: minutes })); if (minutes > 2) setTwoMinDismissed(false); }} className="flex-1 py-2.5 rounded-xl text-[11px] font-medium transition-colors" style={{ background: form.estimatedMinutes === minutes ? (minutes <= 2 ? 'var(--pd-green)' : 'var(--pd-focus)') : 'var(--pd-panel-2)', color: form.estimatedMinutes === minutes ? 'white' : 'var(--pd-muted)' }}>{minutes}m</button>
            ))}
          </div>
          {/* 2-Minute Rule Nudge */}
          {form.estimatedMinutes <= 2 && !twoMinDismissed && !twoMinTimer?.active ? (
            <div data-testid="two-min-rule-nudge" className="mt-2 flex items-center gap-2 px-3 py-2.5 rounded-xl animate-slide-up" style={{ background: 'var(--pd-green-soft)', border: '1px solid rgba(21,128,61,0.15)' }}>
              <Timer className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--pd-green)' }} />
              <div className="flex-1 min-w-0">
                <div className="text-[11px] font-semibold" style={{ color: 'var(--pd-green)' }}>Under 2 mins — do it now?</div>
                <div className="text-[10px]" style={{ color: 'var(--pd-muted)' }}>GTD rule: quick tasks shouldn't sit in your queue</div>
              </div>
              <div className="flex gap-1.5">
                <button type="button" data-testid="two-min-do-now" onClick={() => { onSave({ ...form, status: 'COMPLETED' }); toast.success('Done! Cleared from queue.'); }} className="px-2 py-1 rounded-lg text-[10px] font-semibold text-white" style={{ background: 'var(--pd-green)' }}>
                  <CheckCircle2 className="w-3 h-3 inline mr-0.5" />Done
                </button>
                <button type="button" data-testid="two-min-start-timer" onClick={() => setTwoMinTimer({ active: true, seconds: 120 })} className="px-2 py-1 rounded-lg text-[10px] font-semibold" style={{ background: 'var(--pd-focus)', color: 'white' }}>
                  <Timer className="w-3 h-3 inline mr-0.5" />2m timer
                </button>
              </div>
            </div>
          ) : null}
          {/* Active 2-min timer */}
          {twoMinTimer?.active ? (
            <div data-testid="two-min-timer-active" className="mt-2 flex items-center gap-3 px-3 py-2.5 rounded-xl" style={{ background: 'var(--pd-focus)', border: '1px solid rgba(37,99,235,0.3)' }}>
              <Timer className="w-5 h-5 text-white animate-pulse" />
              <div className="flex-1">
                <div className="text-[13px] font-bold text-white tabular-nums">{Math.floor(twoMinTimer.seconds / 60)}:{String(twoMinTimer.seconds % 60).padStart(2, '0')}</div>
                <div className="text-[10px] text-white/70">Focus! Auto-completes when done.</div>
              </div>
              <button type="button" onClick={() => { setTwoMinTimer(null); onSave({ ...form, status: 'COMPLETED' }); toast.success('Completed early!'); }} className="px-3 py-1.5 rounded-lg text-[10px] font-semibold" style={{ background: 'var(--pd-panel)', color: 'var(--pd-focus)' }}>
                Done early
              </button>
            </div>
          ) : null}
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-[0.08em] font-semibold block mb-1.5" style={{ color: 'var(--pd-subtle)' }}><Calendar className="w-3 h-3 inline mr-1" />Due date</label>
          <input data-testid="task-due-date-input" type="date" value={form.dueDate} onChange={(e) => setForm((prev) => ({ ...prev, dueDate: e.target.value }))} className="w-full px-3 py-3 rounded-[16px] text-sm" style={{ border: '1px solid var(--pd-border)', background: 'var(--pd-panel-solid)' }} />
        </div>
      </div>
    </>
  );
}
